import React, { createContext, useCallback, useContext, useMemo, useState } from "react"
import useAddSalesApis from "../../../../../hooks/addSalesCustomHook/addSalesCustomHook"
import { Form } from "antd"

const AddSalesContext = createContext()

export const AddSalesContextProvider = ({ children }) => {
  const addSalesApis = useAddSalesApis()
  const [form] = Form.useForm()

  const { leadSalesInfo } = addSalesApis

  const formWatchers = {
    unit: Form.useWatch("unit", form),
    amount: Form.useWatch("amount", form),
    cdpCommitment: Form.useWatch("cdpCommitment", form),
    saleType: Form.useWatch("saleType", form),
    project: Form.useWatch("project", form),
    bookingPlatform: Form.useWatch("bookingPlatform", form),
  }

  const billingType = useMemo(
    () => leadSalesInfo?.contracts?.[0]?.project?.BillingType?.key || formWatchers?.project?.BillingType?.key,
    [leadSalesInfo, formWatchers?.project?.BillingType?.key]
  )

  const lastPaymentStatus = useMemo(
    () => leadSalesInfo?.contracts?.[0]?.Payments?.at(-1)?.PaymentStatus?.key,
    [leadSalesInfo]
  )

  const lastPaymentType = useMemo(
    () => leadSalesInfo?.contracts?.[0]?.Payments?.at(-1)?.PaymentType?.key,
    [leadSalesInfo]
  )

  const isEscrowSelfFinance = useMemo(
    () => billingType === "escrow" && formWatchers?.saleType === "self_finance",
    [billingType, formWatchers?.saleType]
  )

  const showNextPayment = useMemo(
    () => !isEscrowSelfFinance || lastPaymentStatus === "rejected" || !!leadSalesInfo?.isFirstSalesTask,
    [isEscrowSelfFinance, lastPaymentStatus, leadSalesInfo]
  )

  const [stateManager, setStateManager] = useState({
    project: {},
    unit: {},
    unitPrice: 0,
    dealPrice: 0,
    discountAmount: 0,
    featureTable: {},
    provisionalFormFilled: false,
  })

  const [optionalSelections, setOptionalSelections] = useState(() => {
    const fees =
      formWatchers?.project?.ProjectFinancial?.vasFees || leadSalesInfo?.contracts?.[0]?.snapshot?.vasFees || []
    const initial = {}
    fees?.forEach(fee => {
      if (!fee.isMandatory) {
        initial[fee?.key] = fee?.isMandatory
      }
    })
    return initial
  })

  const vasFees = useMemo(() => {
    const fees = formWatchers?.project?.ProjectFinancial?.vasFees || leadSalesInfo?.contracts?.[0]?.snapshot?.vasFees

    if (!fees) return 0

    return fees.reduce((total, fee) => {
      if (!fee) return total
      const isSelectedMandatory = fee?.isMandatory
      const isSelectedOptional = !fee?.isMandatory && optionalSelections?.[fee.key]
      if (!isSelectedMandatory && !isSelectedOptional) return total
      const { amountBy, amountValue = 0, taxPercentage = 0 } = fee
      const dealPrice = stateManager?.dealPrice || 0
      const isAmountType = amountBy === "amount"

      const baseValue = isAmountType ? amountValue : (dealPrice * amountValue) / 100

      const tax = (baseValue * taxPercentage) / 100

      return total + baseValue + tax
    }, 0)
  }, [
    formWatchers?.project?.ProjectFinancial?.vasFees,
    leadSalesInfo?.contracts?.[0]?.snapshot,
    stateManager?.dealPrice,
    optionalSelections,
  ])

  const vasEnabled = useMemo(
    () =>
      formWatchers?.project?.ProjectFinancial?.vasFeeEnabled ?? leadSalesInfo?.contracts?.[0]?.snapshot?.vasFeeEnabled,
    [formWatchers?.project?.ProjectFinancial?.vasFeeEnabled, leadSalesInfo?.contracts]
  )

  const minTokenDetails = useMemo(() => {
    const isMortgage = formWatchers?.saleType === "mortgage"
    return {
      minToken: isMortgage ? formWatchers?.project?.minTokenMortgage : formWatchers?.project?.minToken,
      minTokenUnit: isMortgage ? formWatchers?.project?.minTokenUnitMortgage : formWatchers?.project?.minTokenUnit,
      tokenWithUnit: isMortgage ? formWatchers?.project?.mortgageTokenWithUnit : formWatchers?.project?.tokenWithUnit,
    }
  }, [formWatchers?.saleType, formWatchers?.project])

  const [isEditable, setIsEditable] = useState(false)
  const [isImageUploading, setIsImageUploading] = useState(false)
  const [client, setClient] = useState()
  const [lead, setLead] = useState()

  const updateStateManager = useCallback(newData => setStateManager(prev => ({ ...prev, ...newData })), [])

  const updateIsEditable = val => {
    setIsEditable(val)
  }

  const setImageUploading = val => {
    setIsImageUploading(val)
  }

  const value = useMemo(
    () => ({
      form,
      formWatchers, // Form Watchers
      addSalesApis, // Common APIs

      stateManager,
      updateStateManager,

      isEditable,
      updateIsEditable,

      isImageUploading,
      setImageUploading,

      client,
      setClient,

      lead,
      setLead,

      minTokenDetails,
      billingType,
      lastPaymentStatus,
      lastPaymentType,
      isEscrowSelfFinance,
      showNextPayment,
      vasEnabled,
      vasFees,
      optionalSelections,
      setOptionalSelections,
    }),
    [
      form,
      formWatchers,
      addSalesApis,

      stateManager,
      updateStateManager,

      isEditable,
      updateIsEditable,

      isImageUploading,
      setImageUploading,

      client,
      setClient,

      lead,
      setLead,

      minTokenDetails,
      billingType,
      lastPaymentStatus,
      lastPaymentType,
      isEscrowSelfFinance,
      showNextPayment,
      vasEnabled,
      vasFees,
      optionalSelections,
      setOptionalSelections,
    ]
  )

  return <AddSalesContext.Provider value={value}>{children}</AddSalesContext.Provider>
}

export const useAddSalesContext = () => useContext(AddSalesContext)
